import { adApiRequest, normaliseAdSubject, type AdApiResult, type AdSubject, type AdSubjectType } from './banner-kit-api';
import { normaliseLandingPage, type LandingPage } from './landing-page-api';

/**
 * Tracking links, minted and stored by spark-creatives (`/creatives/tenant/{t}/tracking-links`), with no dependency on
 * the affiliates platform.
 *
 * A link is the asset version's destination plus `?aff_id=<affiliate>_<campaign>_<site>`: the casino keeps `aff_id`,
 * signup splits it on `_` into the player's affiliate / campaign / affiliate_site_id, and the Owners Hub Stats
 * `daily_affiliate_activity` data set aggregates on exactly those three, so every registration / FTD / deposit through
 * a link is reported against its `campaign` value. spark-creatives mints the three values itself: short lowercase
 * base36 tokens that start with a letter and never contain `_` (so they can never be split wrongly, and never collide
 * with the integer numbers the affiliates platform issues):
 *  - `affiliate`: one per owner (the owner's in-house marketing), the same on every link they make;
 *  - `site`: one per brand;
 *  - `campaign`: one per link, i.e. per asset version (a landing page's own link: one per page).
 * The three never change once minted: moving a link to another destination keeps its `aff_id`.
 *
 * Destinations: the casino (the URL the front end computes: the promotion's page on the brand domain, else the
 * homepage), or a landing page. A Studio landing page is pinned by its spark id (`landing_page_id`), never by its
 * public `lpid` (a republish after an unpublish mints a new one), and spark-creatives builds the link from the page's
 * current address. A live page Studio did not make has no spark id: it is stored by its URL alone.
 *
 * Wire types mirror the swagger definitions (snake_case); reads are normalised defensively.
 */

/** The kind of asset a link belongs to. */
export type TrackingLinkKind = 'video' | 'kit' | 'playable' | 'banner' | 'landing_page';

/** Where a link sends its visitors. */
export type TrackingDestinationType = 'casino' | 'landing_page';

/** The Studio landing page a link lands on, as it is now. `draft`: never published (e.g. a quick landing page still
 *  being built). */
export interface TrackingLinkLanding {
  id: string;
  name: string;
  state: 'draft' | 'live' | 'unpublished' | 'failed';
  /** The page's address (`https://lp.brand.com/welcome`), '' before its first publish. */
  url: string;
}

/** The asset a link belongs to, as it is now (joined by spark-creatives from the creatives row). */
export interface TrackingLinkAsset {
  id: string;
  /** The asset's current title ('' when it has none). */
  name: string;
  /** A thumbnail of the asset ('' when it has none yet). */
  image_url: string;
  /** The creatives row's media type (`video`, `banner_kit`, `playable`, `landing_page`, …). */
  media_type: string;
  /** The asset was archived or deleted since the link was made (its link still attributes). */
  archived: boolean;
}

export interface TrackingLink {
  id: string;
  tenant_id: string;
  /** The asset (video ad, banner kit, mini-game, HTML5 banner, or the landing page itself for its own link). */
  creative_id: string;
  /** The asset version the link is for (a landing page's own link: `page`). */
  version_key: string;
  kind: TrackingLinkKind;
  /** The link's display name (the asset's title and version). */
  name: string;
  affiliate: string;
  campaign: string;
  site: string;
  /** `<affiliate>_<campaign>_<site>`. */
  aff_id: string;
  destination_type: TrackingDestinationType;
  /** The Studio landing page's spark id ('' for the casino, or for a live page Studio did not make). */
  landing_page_id: string;
  /** The destination as last written (the casino URL, or the landing page's address at the time). */
  destination_url: string;
  /** What the owner copies: the destination with `aff_id` appended ('' when there is no destination yet, e.g. a quick
   *  landing page not published yet). */
  url: string;
  /** Present when the link lands on a Studio landing page. */
  landing_page?: TrackingLinkLanding;
  /** The asset the link belongs to (absent when its row is gone). */
  asset?: TrackingLinkAsset;
  created_at: string;
  updated_at: string;
}

/** Create the asset version's link (minting its values), or update its name and destination (keeping them). Keyed by
 *  `creative_id` + `version_key`. */
export interface TrackingLinkUpsert {
  creative_id: string;
  version_key: string;
  kind: TrackingLinkKind;
  name: string;
  destination_type: TrackingDestinationType;
  landing_page_id?: string;
  destination_url: string;
}

/** Move a link to another destination (same `aff_id`). */
export interface TrackingLinkDestination {
  destination_type: TrackingDestinationType;
  landing_page_id?: string;
  destination_url: string;
}

/** List filters; several narrow together, none = every link of the brand. */
export interface TrackingLinkQuery {
  creativeId?: string;
  landingPageId?: string;
  id?: string;
}

/** "Quick landing page": Lumio builds a landing page for the asset's subject (default template, the brand's default
 *  marketing offer, the website's languages), publishes it when the run finishes, and points the asset version's link
 *  at it. `dry_run`: only the price and the defaults it would use, nothing created. */
export interface QuickLandingRequest {
  creative_id: string;
  version_key: string;
  kind: TrackingLinkKind;
  link_name: string;
  subject_type: AdSubjectType;
  subject: AdSubject;
  /** '' = the default template. */
  template_id?: string;
  dry_run?: boolean;
}

export interface QuickLandingAnswer {
  /** The run's price in credits. */
  credits: number;
  template_id: string;
  /** The address path it publishes at (`/welcome-bonus`); a taken one gets a numeric suffix at publish time. */
  path: string;
  /** Absent on a dry run. */
  landing_page?: LandingPage;
  link?: TrackingLink;
  job_id?: string;
}

/** The composer route's query keys when a landing page is made FOR an asset's link ("New landing page…"): once the new
 *  page is published, the composer points that asset version's link at it and offers the way back. */
export const LINK_FOR_PARAMS = {
  creativeId: 'for_creative',
  versionKey: 'for_version',
  kind: 'for_kind',
  name: 'for_name',
  /** The asset's subject (promotion banner id / game id) the page should feature. */
  subjectType: 'for_subject_type',
  subjectId: 'for_subject_id',
  /** Where "Back to …" returns (path + query of the asset's composer). */
  returnTo: 'return_to',
} as const;

/* ---------- normalisation ---------- */

type Rec = Record<string, unknown>;

const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');

const KINDS: readonly TrackingLinkKind[] = ['video', 'kit', 'playable', 'banner', 'landing_page'];
const LANDING_STATES: readonly TrackingLinkLanding['state'][] = ['draft', 'live', 'unpublished', 'failed'];

const normaliseAsset = (raw: unknown): TrackingLinkAsset | undefined => {
  if (!isRec(raw) || !str(raw.id)) return undefined;
  return {
    id: str(raw.id),
    name: str(raw.name),
    image_url: str(raw.image_url),
    media_type: str(raw.media_type),
    archived: raw.archived === true,
  };
};

const normaliseLanding = (raw: unknown): TrackingLinkLanding | undefined => {
  if (!isRec(raw) || !str(raw.id)) return undefined;
  const state = str(raw.state) as TrackingLinkLanding['state'];
  return {
    id: str(raw.id),
    name: str(raw.name),
    state: LANDING_STATES.includes(state) ? state : 'draft',
    url: str(raw.url),
  };
};

export const normaliseTrackingLink = (raw: unknown): TrackingLink => {
  const r = isRec(raw) ? raw : {};
  const kind = str(r.kind) as TrackingLinkKind;
  return {
    id: str(r.id),
    tenant_id: str(r.tenant_id),
    creative_id: str(r.creative_id),
    version_key: str(r.version_key),
    kind: KINDS.includes(kind) ? kind : 'video',
    name: str(r.name),
    affiliate: str(r.affiliate),
    campaign: str(r.campaign),
    site: str(r.site),
    aff_id: str(r.aff_id),
    destination_type: str(r.destination_type) === 'landing_page' ? 'landing_page' : 'casino',
    landing_page_id: str(r.landing_page_id),
    destination_url: str(r.destination_url),
    url: str(r.url),
    landing_page: normaliseLanding(r.landing_page),
    asset: normaliseAsset(r.asset),
    created_at: str(r.created_at),
    updated_at: str(r.updated_at),
  };
};

const normaliseQuick = (raw: unknown): QuickLandingAnswer => {
  const r = isRec(raw) ? raw : {};
  return {
    credits: typeof r.credits === 'number' ? r.credits : Number(r.credits) || 0,
    template_id: str(r.template_id),
    path: str(r.path),
    landing_page: isRec(r.landing_page) ? normaliseLandingPage(r.landing_page) : undefined,
    link: isRec(r.link) ? normaliseTrackingLink(r.link) : undefined,
    job_id: str(r.job_id) || undefined,
  };
};

/* ---------- endpoints ---------- */

const root = (tenantId: number): string => `/creatives/tenant/${encodeURIComponent(String(tenantId))}/tracking-links`;

/** The brand's links, narrowed by the query (`{ links: [...] }` on the wire). */
export const fetchTrackingLinks = async (
  tenantId: number,
  query: TrackingLinkQuery = {},
  signal?: AbortSignal,
): Promise<AdApiResult<TrackingLink[]>> => {
  const qs = new URLSearchParams();
  if (query.creativeId) qs.set('creative_id', query.creativeId);
  if (query.landingPageId) qs.set('landing_page_id', query.landingPageId);
  if (query.id) qs.set('id', query.id);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await adApiRequest<Rec>('get', `${root(tenantId)}${suffix}`, undefined, signal);
  if (!res.ok) return res;
  const rows = Array.isArray(res.data.links) ? res.data.links : [];
  return { ok: true, data: rows.map(normaliseTrackingLink).filter((l) => l.id !== '') };
};

/** Create or update the asset version's link. */
export const upsertTrackingLink = async (
  tenantId: number,
  body: TrackingLinkUpsert,
  signal?: AbortSignal,
): Promise<AdApiResult<TrackingLink>> => {
  const res = await adApiRequest<Rec>('post', root(tenantId), body, signal);
  return res.ok ? { ok: true, data: normaliseTrackingLink(res.data) } : res;
};

/** Move a link to another destination; its `aff_id` stays. */
export const retargetTrackingLink = async (
  tenantId: number,
  id: string,
  body: TrackingLinkDestination,
  signal?: AbortSignal,
): Promise<AdApiResult<TrackingLink>> => {
  const res = await adApiRequest<Rec>('post', `${root(tenantId)}/${encodeURIComponent(id)}/destination`, body, signal);
  return res.ok ? { ok: true, data: normaliseTrackingLink(res.data) } : res;
};

/** Start a quick landing page (or, with `dry_run`, read its price). A refusal carries the service's `error` code:
 *  `no_offer` (the brand has no marketing offer to promote), `insufficient_credits` (with `needed`). */
export const quickCreateLandingPage = async (
  tenantId: number,
  body: QuickLandingRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<QuickLandingAnswer>> => {
  const res = await adApiRequest<Rec>(
    'post',
    `/creatives/tenant/${encodeURIComponent(String(tenantId))}/landing-pages/quick`,
    { ...body, subject: normaliseAdSubject(body.subject) },
    signal,
  );
  return res.ok ? { ok: true, data: normaliseQuick(res.data) } : res;
};
